import CloseIcon from "@mui/icons-material/Close";
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	Grid,
	InputAdornment,
	LinearProgress,
	MenuItem,
	IconButton,
	Typography,
	Alert,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import * as yup from "yup";
import { ToastContent } from "../../../../shared/components";
import { useToast } from "../../../../shared/hooks";
import { useVForm, VForm, VTextField } from "../../../../shared/forms";
import { VMaskTextField } from "../../../../shared/forms/VMaskTextField";
import { globalFormatCurrencyBRL, globalRemoveMaskReal2 } from "../../../../shared/utils";
import { IInstallmentGenerate, ProposalService } from "../../../../shared/services/api/proposal/ProposalService";
import { AutoCompleteProposal } from "../AutoCompleteProposalComission";
import { IInstallmentTable } from "../../types/types";

interface IVFormErros {
	[key: string]: string;
}

type FormData = {
	proposalId: string;
	obs: string;
	typeAmount: "discount" | "add" | "parcel";
	amount: string;
	dueDate: string;
};

interface Props {
	open: boolean;
	onClose: () => void;
	row: IInstallmentTable;
	dataIni: string;
	dataFim: string;
	onSuccess?: () => void;
}

const maxDateStr = (a: string, b: string) => (a >= b ? a : b);

export const ModalInstallmentLaunch = ({ open, onClose, row, dataIni, dataFim, onSuccess }: Props) => {
	const { formRef } = useVForm();
	const toastHandler = useToast();

	const [isLoading, setIsLoading] = useState(false);
	const [selectedProposalId, setSelectedProposalId] = useState<string>("");
	const [selectedTypeAmount, setSelectedTypeAmount] = useState<FormData["typeAmount"]>("parcel");
	const [amountInput, setAmountInput] = useState<string>("0,00");
	const [formKey, setFormKey] = useState(0);

	const todayStr = useMemo(() => new Date().toISOString().split("T")[0], []);
	const minAllowedDate = useMemo(() => maxDateStr(todayStr, dataIni), [todayStr, dataIni]);
	const initialDueDate = useMemo(() => (todayStr < minAllowedDate ? minAllowedDate : todayStr), [todayStr, minAllowedDate]);
	const initialData = useMemo(
		() => ({
			proposalId: "",
			obs: "",
			typeAmount: "parcel" as const,
			amount: "0,00",
			dueDate: initialDueDate,
		}),
		[initialDueDate]
	);

	const saldoAPagar = useMemo(() => {
		// Deve refletir exatamente o "Valor Total" exibido na tabela.
		const total = Number(row.sum_amount);
		return Number.isFinite(total) ? Math.max(0, total) : 0;
	}, [row.sum_amount]);

	const parsedAmountNumber = useMemo(() => {
		const cleaned = globalRemoveMaskReal2(String(amountInput || "0"));
		const n = Number(cleaned);
		return Number.isFinite(n) ? n : 0;
	}, [amountInput]);

	const shouldWarnDiscountOverSaldo = useMemo(() => {
		return selectedTypeAmount === "discount" && parsedAmountNumber >= saldoAPagar && saldoAPagar > 0;
	}, [selectedTypeAmount, parsedAmountNumber, saldoAPagar]);

	useEffect(() => {
		if (!open) return;
		setSelectedProposalId("");
		setSelectedTypeAmount("parcel");
		setAmountInput("0,00");
		setFormKey((prev) => prev + 1);
	}, [open]);

	const clampDueDate = (value: string) => {
		const dueDateOnly = String(value || "").substring(0, 10);
		if (!dueDateOnly || dueDateOnly.length !== 10) return;
		if (minAllowedDate && dueDateOnly < minAllowedDate) {
			formRef.current?.setFieldValue("dueDate", minAllowedDate);
			toastHandler.present({
				type: "warning",
				position: "top-right",
				messageOrContent: <ToastContent title="Aviso" content="Não é permitido lançar valores retroativos." />,
			});
		}
	};

	useEffect(() => {
		formRef.current?.setFieldValue("proposalId", selectedProposalId);
	}, [selectedProposalId, formRef]);

	const schema: yup.SchemaOf<FormData> = yup.object({
		proposalId: yup.string().required("Campo obrigatório"),
		obs: yup.string().required("Campo obrigatório"),
		typeAmount: yup.mixed<FormData["typeAmount"]>().oneOf(["discount", "add", "parcel"]).required("Campo obrigatório"),
		amount: yup.string().required("Campo obrigatório"),
		dueDate: yup.string().required("Campo obrigatório"),
	});

	const handleSubmit = async (data: FormData) => {
		try {
			setIsLoading(true);

			const dueDateOnly = String(data.dueDate || "").substring(0, 10);
			if (minAllowedDate && dueDateOnly < minAllowedDate) {
				formRef.current?.setFieldValue("dueDate", minAllowedDate);
				throw new Error("Não é permitido lançar valores retroativos.");
			}

			const cleanedAmount = globalRemoveMaskReal2(String(data.amount || "0"));
			const payload: Omit<IInstallmentGenerate, "id"> = {
				status: "pending",
				paymentMethod: "billet",
				type: "monthly_payment",
				proposalId: selectedProposalId,
				clientId: row.clientId,
				amount: Number(cleanedAmount).toFixed(2),
				dueDate: `${data.dueDate} 03:00:00`,
				obs: data.obs,
				typeAmount: data.typeAmount,
			};

			await schema.validate({ ...data, proposalId: selectedProposalId }, { abortEarly: false });

			const result = await ProposalService.createInstallment(payload);
			if (result instanceof Error) {
				throw result;
			}

			toastHandler.present({
				type: "success",
				position: "top-right",
				messageOrContent: "Lançamento efetuado com sucesso!",
			});

			onSuccess?.();
			onClose();
		} catch (error) {
			const validationErrors: IVFormErros = {};
			if (error instanceof yup.ValidationError) {
				error.inner.forEach((err) => {
					if (err.path) validationErrors[err.path] = err.message;
				});
				formRef.current?.setErrors(validationErrors);
			} else {
				const message = error instanceof Error ? error.message : "Erro ao efetuar lançamento.";
				toastHandler.present({
					type: "error",
					position: "top-right",
					messageOrContent: <ToastContent title="Erro" content={message} />,
				});
			}
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
			<DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
				<Typography variant="h6" fontWeight={400} color="primary.main">
					Lançamento de valores
				</Typography>
				<IconButton onClick={onClose} size="small">
					<CloseIcon />
				</IconButton>
			</DialogTitle>

			<Divider />

			<DialogContent sx={{ px: 4, pt: 3 }}>
				<VForm
					key={formKey}
					ref={formRef}
					onSubmit={handleSubmit}
					initialData={initialData}
					onPointerEnterCapture={() => {}}
					onPointerLeaveCapture={() => {}}
					placeholder=""
				>
					<Grid container spacing={2}>
						{isLoading && (
							<Grid item xs={12}>
								<LinearProgress />
							</Grid>
						)}

						<Grid item xs={12}>
							<Typography variant="body2" color="text.secondary">
								{row.name}
							</Typography>
						</Grid>

						<Grid item xs={12}>
							<AutoCompleteProposal
								clientId={row.clientId}
								labelSelect="Garantia"
								placeholder="Selecione uma garantia"
								namecampo="proposalId"
								onChange={(value) => setSelectedProposalId(value?.proposal_id ?? "")}
							/>
						</Grid>

						<Grid item xs={12}>
							<VTextField fullWidth label="Descrição" name="obs" placeholder="Ex.: ajuste de parcela, cobrança adicional, desconto aplicado..." />
						</Grid>

						<Grid item xs={4}>
							<VTextField fullWidth label="Tipo" name="typeAmount" select onChange={(e: any) => setSelectedTypeAmount(e?.target?.value)}>
								<MenuItem value="add">Acréscimo</MenuItem>
								<MenuItem value="discount">Desconto</MenuItem>
								<MenuItem value="parcel">Parcela</MenuItem>
							</VTextField>
						</Grid>

						<Grid item xs={4}>
							<VMaskTextField
								fullWidth
								label="Valor"
								name="amount"
								mask="real"
								placeholder="0,00"
								onChange={(e: any) => setAmountInput(e?.target?.value ?? "")}
								onBlur={(e: any) => setAmountInput(e?.target?.value ?? "")}
								InputProps={{
									startAdornment: <InputAdornment position="start">R$</InputAdornment>,
								}}
							/>
						</Grid>

						<Grid item xs={4}>
							<VTextField
								fullWidth
								label="Vencimento"
								name="dueDate"
								type="date"
								onChange={(e: any) => clampDueDate(e?.target?.value)}
								onBlur={(e: any) => clampDueDate(e?.target?.value)}
								inputProps={{
									min: minAllowedDate,
								}}
							/>
						</Grid>

						{shouldWarnDiscountOverSaldo && (
							<Grid item xs={12}>
								<Alert severity="warning" variant="standard">
									O desconto informado ({globalFormatCurrencyBRL(parsedAmountNumber.toFixed(2))}) é maior ou igual ao saldo a pagar (
									{globalFormatCurrencyBRL(saldoAPagar.toFixed(2))}). Confira antes de confirmar.
								</Alert>
							</Grid>
						)}

						<Grid item xs={12}>
							<Typography variant="caption" color="text.secondary">
								Saldo a pagar: {globalFormatCurrencyBRL(saldoAPagar.toFixed(2))}
							</Typography>
						</Grid>
					</Grid>
				</VForm>
			</DialogContent>

			<DialogActions sx={{ px: 4, pb: 3 }}>
				<Button variant="outlined" color="inherit" onClick={onClose} disabled={isLoading}>
					Cancelar
				</Button>
				<Button variant="contained" color="primary" disabled={isLoading || !selectedProposalId} onClick={() => formRef.current?.submitForm()}>
					Confirmar
				</Button>
			</DialogActions>
		</Dialog>
	);
};
